import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Play } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/loading-state'
import { listEventMappings, deleteEventMapping } from '../api/derivationConfig'
import { EventMappingModal } from './EventMappingModal'
import type { EventMapping } from '../types'

interface EventMappingsTabProps {
  accId?: number
}

export function EventMappingsTab({ accId }: EventMappingsTabProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editTarget, setEditTarget] = useState<EventMapping | undefined>()
  const [deleteConfirm, setDeleteConfirm] = useState<EventMapping | null>(null)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  )

  const { data: mappings = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['event-mappings', accId],
    queryFn: () => listEventMappings(accId),
  })

  const deleteMutation = useMutation({
    mutationFn: (m: EventMapping) =>
      deleteEventMapping(m.accId, m.eventType, m.category, m.eventName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-mappings'] })
      setDeleteConfirm(null)
      setFeedback({ type: 'success', message: 'Mapping deleted.' })
      setTimeout(() => setFeedback(null), 3000)
    },
    onError: (err: Error) => {
      setFeedback({ type: 'error', message: err.message })
      setDeleteConfirm(null)
    },
  })

  const handleEdit = (m: EventMapping) => {
    setEditTarget(m)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditTarget(undefined)
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Event Mappings</h2>
          <p className="text-sm text-gray-500">
            Map intermediate field names to coded schema fields for each event type.
          </p>
        </div>
        <Button size="sm" onClick={() => { setEditTarget(undefined); setShowModal(true) }}>
          <Plus className="h-4 w-4 mr-1.5" />
          New Mapping
        </Button>
      </div>

      {feedback && (
        <div
          className={`mb-4 text-sm px-4 py-2 rounded-lg ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {feedback.message}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        {isLoading ? (
          <LoadingState message="Loading event mappings…" />
        ) : isError ? (
          <ErrorState
            error={error as Error}
            title="Failed to load event mappings"
            onRetry={() => refetch()}
          />
        ) : mappings.length === 0 ? (
          <EmptyState
            title="No event mappings"
            description="Create your first event mapping to get started."
            action={{ label: 'New Mapping', onClick: () => setShowModal(true) }}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 rounded-t-xl">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Acc ID</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Event Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Event Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Mappings</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Test</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m, idx) => (
                  <tr
                    key={`${m.accId}-${m.eventType}-${m.category}-${m.eventName}`}
                    className={`border-b last:border-0 hover:bg-gray-50 transition-colors ${
                      idx % 2 === 0 ? '' : 'bg-gray-50/40'
                    }`}
                  >
                    <td className="px-4 py-3 font-mono text-gray-700">{m.accId}</td>
                    <td className="px-4 py-3 text-gray-700">{m.eventType}</td>
                    <td className="px-4 py-3 text-gray-700">{m.category}</td>
                    <td className="px-4 py-3 text-gray-700">{m.eventName}</td>
                    <td className="px-4 py-3">
                      <Badge variant="info">{Object.keys(m.mappings).length} field{Object.keys(m.mappings).length !== 1 ? 's' : ''}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/developer-console', { state: { mapping: m, tab: 'events-browser' } })}
                        className="text-primary border-primary hover:bg-primary/5"
                        title="Test in Developer Console"
                      >
                        <Play className="h-3.5 w-3.5 mr-1" />
                        Use
                      </Button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {deleteConfirm === m ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-xs text-gray-500">Confirm delete?</span>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteMutation.mutate(m)}
                            disabled={deleteMutation.isPending}
                          >
                            Yes
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            No
                          </Button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(m)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirm(m)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <EventMappingModal existing={editTarget} onClose={handleCloseModal} />
      )}
    </div>
  )
}
